import type { FrameSnapshotProvider, SnapshotEntry } from '../types';

const DB_NAME = 'streamlain-frame-snapshots';
const DB_VERSION = 1;
const STORE_NAME = 'snapshots';
const TTL_DAYS = 30;
const MAX_ENTRIES = 200;

/**
 * Frame Snapshot Service
 *
 * Strategy: DOM Clone + Canvas toDataURL (NO html2canvas!)
 *
 * Storage: Isolated IndexedDB (NOT synced to cloud!)
 * - Database: 'streamlain-frame-snapshots' (separate from nbstore)
 * - TTL: 30 days
 * - Max entries: 200 (LRU eviction)
 *
 * Flow:
 * 1. Clone the rendered DOM element
 * 2. Replace Custom Elements with static divs
 * 3. Convert canvas elements to PNG via toDataURL()
 * 4. Convert blob: URLs to data: URLs
 * 5. Serialize to HTML string
 * 6. Store in IndexedDB
 */
export class FrameSnapshotService implements FrameSnapshotProvider {
  /** In-memory cache for fast access (backed by IndexedDB) */
  private readonly memoryCache = new Map<string, SnapshotEntry>();

  /** IndexedDB database instance */
  private db: IDBDatabase | null = null;

  /** Database initialization promise (includes pre-load) */
  private readonly dbReady: Promise<IDBDatabase | null>;

  /** Frames currently being captured */
  private readonly pendingCaptures = new Set<string>();

  /** Known frame IDs in current doc (for orphan detection) */
  private knownFrameIds = new Set<string>();

  /** Current document ID */
  private currentDocId: string | null = null;

  constructor() {
    this.dbReady = this._initDB()
      .then(async db => {
        await this._preloadFromDB();
        return db;
      })
      .catch(error => {
        console.warn(
          '[FrameSnapshot] IndexedDB unavailable; using memory-only snapshots.',
          error
        );
        return null;
      });
  }

  /**
   * Pre-load all valid entries from IndexedDB into memory cache.
   * Called on service initialization to ensure cache hits after page reload.
   * NOTE: Uses this.db directly (not _getAllFromDB) to avoid deadlock!
   */
  private async _preloadFromDB(): Promise<void> {
    if (!this.db) return;

    try {
      const entries = await new Promise<SnapshotEntry[]>((resolve, reject) => {
        const tx = this.db!.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const now = Date.now();

      for (const entry of entries) {
        if (now > entry.expiresAt) {
          const tx = this.db!.transaction(STORE_NAME, 'readwrite');
          tx.objectStore(STORE_NAME).delete(entry.frameId);
        } else {
          this.memoryCache.set(entry.frameId, entry);
        }
      }
    } catch {
      // Pre-load failed — entries will be loaded on demand
    }
  }

  /**
   * Initialize IndexedDB
   */
  private _initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[FrameSnapshot] IndexedDB open failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'frameId',
          });
          store.createIndex('docId', 'docId', { unique: false });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  /**
   * Set current document context (for orphan cleanup)
   */
  setDocContext(docId: string, frameIds: string[]): void {
    this.currentDocId = docId;
    this.knownFrameIds = new Set(frameIds);

    this._cleanupOrphansForDoc(docId).catch(() => {
      /* orphan cleanup is best-effort */
    });
  }

  /**
   * Check if snapshot is available (memory cache only for speed)
   */
  isAvailable(frameId: string): boolean {
    return this.memoryCache.has(frameId);
  }

  /**
   * Get snapshot from memory cache
   */
  getSnapshot(frameId: string): string | null {
    const entry = this.memoryCache.get(frameId);
    if (!entry) return null;

    // Check TTL
    if (Date.now() > entry.expiresAt) {
      this.memoryCache.delete(frameId);
      this._deleteFromDB(frameId).catch(() => {
        /* ignore */
      });
      return null;
    }

    return entry.html;
  }

  /**
   * Load snapshot from IndexedDB into memory cache
   */
  async loadFromDB(frameId: string): Promise<string | null> {
    if (this.memoryCache.has(frameId)) {
      return this.getSnapshot(frameId);
    }

    try {
      await this.dbReady;
      const entry = await this._getFromDB(frameId);

      if (!entry) return null;

      // Check TTL
      if (Date.now() > entry.expiresAt) {
        await this._deleteFromDB(frameId);
        return null;
      }

      this.memoryCache.set(frameId, entry);
      return entry.html;
    } catch {
      return null;
    }
  }

  /**
   * Capture a frame and store in IndexedDB
   *
   * @param backgroundColor - Frame background color from model (Shadow DOM doesn't clone)
   */
  async capture(
    frameId: string,
    element: Element,
    docId?: string,
    backgroundColor?: string
  ): Promise<void> {
    // Skip if already cached or capturing
    if (this.memoryCache.has(frameId)) return;
    if (this.pendingCaptures.has(frameId)) return;

    this.pendingCaptures.add(frameId);

    try {
      // 1. Collect styles from ORIGINAL Custom Elements BEFORE cloning
      // (Clone is "dead" - connectedCallback/effects don't run)
      const customElementStyles = this._collectCustomElementStyles(element);

      // 2. Deep clone the element
      const clone = element.cloneNode(true) as HTMLElement;

      // 3. Replace Custom Elements with static divs, using collected styles
      this._replaceCustomElements(clone, customElementStyles);

      // 4. Convert canvas elements to images
      const originalCanvases = element.querySelectorAll('canvas');
      const clonedCanvases = clone.querySelectorAll('canvas');

      originalCanvases.forEach((origCanvas, i) => {
        const clonedCanvasOrImg = clonedCanvases[i];
        if (!origCanvas || !clonedCanvasOrImg) return;

        if (clonedCanvasOrImg.tagName === 'CANVAS') {
          try {
            const dataUrl = (origCanvas as HTMLCanvasElement).toDataURL(
              'image/png'
            );
            const img = document.createElement('img');
            img.src = dataUrl;

            const computedStyle = window.getComputedStyle(origCanvas);
            img.style.width = computedStyle.width;
            img.style.height = computedStyle.height;
            img.style.position = computedStyle.position;
            img.style.left = computedStyle.left;
            img.style.top = computedStyle.top;
            img.style.zIndex = computedStyle.zIndex;

            clonedCanvasOrImg.replaceWith(img);
          } catch {
            // Canvas tainted or not readable — skip
          }
        }
      });

      // 5. Convert blob: URLs to data: URLs
      await this._convertBlobUrlsToDataUrls(clone);

      // 6. Serialize to HTML and wrap with background if provided
      const innerHtml = clone.outerHTML;
      const html =
        backgroundColor && backgroundColor !== 'transparent'
          ? `<div style="background:${backgroundColor};width:100%;height:100%;border-radius:2px">${innerHtml}</div>`
          : innerHtml;
      const size = new Blob([html]).size;

      // 7. Create entry with TTL
      const now = Date.now();
      const entry: SnapshotEntry = {
        frameId,
        docId: docId || this.currentDocId || 'unknown',
        html,
        size,
        createdAt: now,
        expiresAt: now + TTL_DAYS * 24 * 60 * 60 * 1000,
      };

      // 8. Store in memory and IndexedDB
      this.memoryCache.set(frameId, entry);
      await this._saveToDB(entry);

      // 9. Check if we need to evict old entries
      await this._enforceMaxEntries();
    } catch (err) {
      console.error('[FrameSnapshot] Capture failed for', frameId, err);
    } finally {
      this.pendingCaptures.delete(frameId);
    }
  }

  /**
   * Invalidate a single frame (on frame change)
   */
  invalidate(frameId: string): void {
    this.memoryCache.delete(frameId);
    this._deleteFromDB(frameId).catch(() => {
      /* ignore */
    });
  }

  /**
   * Invalidate all frames for a document
   */
  async invalidateDoc(docId: string): Promise<void> {
    for (const [frameId, entry] of this.memoryCache) {
      if (entry.docId === docId) {
        this.memoryCache.delete(frameId);
      }
    }
    await this._deleteByDocId(docId);
  }

  /**
   * Invalidate all entries
   */
  async invalidateAll(): Promise<void> {
    this.memoryCache.clear();
    await this._clearDB();
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    memoryCount: number;
    dbCount: number;
    totalSize: number;
  }> {
    let totalSize = 0;
    for (const entry of this.memoryCache.values()) {
      totalSize += entry.size;
    }

    const dbCount = await this._countDB();

    return {
      memoryCount: this.memoryCache.size,
      dbCount,
      totalSize,
    };
  }

  /**
   * Run full cleanup: expired entries + orphans + LRU eviction
   */
  async cleanup(): Promise<{
    expired: number;
    orphans: number;
    evicted: number;
  }> {
    const expired = await this._cleanupExpired();
    const orphans = this.currentDocId
      ? await this._cleanupOrphansForDoc(this.currentDocId)
      : 0;
    const evicted = await this._enforceMaxEntries();

    return { expired, orphans, evicted };
  }

  // ==================== Private: DOM Processing ====================

  /**
   * Collect computed styles from Custom Elements in the ORIGINAL DOM.
   * Must be called BEFORE cloneNode() because clones don't run connectedCallback/effects.
   */
  private _collectCustomElementStyles(
    root: Element
  ): Map<string, { cssText: string; computedBg: string }> {
    const styleMap = new Map<string, { cssText: string; computedBg: string }>();
    const customElements = root.querySelectorAll('*');
    let index = 0;

    customElements.forEach(el => {
      if (el.tagName.includes('-')) {
        const htmlEl = el as HTMLElement;
        const computed = window.getComputedStyle(htmlEl);
        const key = `${el.tagName}-${index}`;
        styleMap.set(key, {
          cssText: htmlEl.style.cssText,
          computedBg: computed.backgroundColor,
        });
        index++;
      }
    });
    return styleMap;
  }

  /**
   * Replace Custom Elements with static divs, applying collected styles.
   */
  private _replaceCustomElements(
    root: HTMLElement,
    collectedStyles?: Map<string, { cssText: string; computedBg: string }>
  ): void {
    const customElements = root.querySelectorAll('*');
    const toReplace: Element[] = [];
    let index = 0;

    customElements.forEach(el => {
      if (el.tagName.includes('-')) {
        toReplace.push(el);
      }
    });

    toReplace.forEach(el => {
      const div = document.createElement('div');
      div.className = el.className + ' static-snapshot-element';

      // Get styles from collected map (from original) or fallback to clone's style
      const key = `${el.tagName}-${index}`;
      const collected = collectedStyles?.get(key);

      if (collected) {
        if (collected.cssText) {
          div.style.cssText = collected.cssText;
        }
        if (
          !div.style.backgroundColor &&
          collected.computedBg &&
          collected.computedBg !== 'rgba(0, 0, 0, 0)'
        ) {
          div.style.backgroundColor = collected.computedBg;
        }
      } else {
        const style = (el as HTMLElement).style;
        if (style.cssText) {
          div.style.cssText = style.cssText;
        }
      }

      Array.from(el.attributes).forEach(attr => {
        if (
          !['data-', 'style', 'class'].some(p => attr.name.startsWith(p)) &&
          attr.name !== 'is'
        ) {
          div.setAttribute(`data-orig-${attr.name}`, attr.value);
        }
      });

      div.setAttribute('data-orig-tag', el.tagName.toLowerCase());

      while (el.firstChild) {
        div.appendChild(el.firstChild);
      }

      el.parentNode?.replaceChild(div, el);
      index++;
    });
  }

  private async _convertBlobUrlsToDataUrls(root: HTMLElement): Promise<void> {
    const imgs = root.querySelectorAll('img[src^="blob:"]');

    const conversions = Array.from(imgs).map(async img => {
      const blobUrl = img.getAttribute('src');
      if (!blobUrl) return;

      try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const dataUrl = await this._blobToDataUrl(blob);
        img.setAttribute('src', dataUrl);
      } catch {
        img.removeAttribute('src');
      }
    });

    await Promise.all(conversions);
  }

  private _blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // ==================== Private: IndexedDB Operations ====================

  private async _getFromDB(frameId: string): Promise<SnapshotEntry | null> {
    await this.dbReady;
    if (!this.db) return null;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(frameId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private async _saveToDB(entry: SnapshotEntry): Promise<void> {
    await this.dbReady;
    if (!this.db) return;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(entry);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async _deleteFromDB(frameId: string): Promise<void> {
    await this.dbReady;
    if (!this.db) return;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(frameId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async _deleteByDocId(docId: string): Promise<void> {
    await this.dbReady;
    if (!this.db) return;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('docId');
      const request = index.openCursor(IDBKeyRange.only(docId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async _clearDB(): Promise<void> {
    await this.dbReady;
    if (!this.db) return;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async _countDB(): Promise<number> {
    await this.dbReady;
    if (!this.db) return 0;
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async _getAllFromDB(): Promise<SnapshotEntry[]> {
    await this.dbReady;
    if (!this.db) return [];
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== Private: Cleanup Operations ====================

  private async _cleanupExpired(): Promise<number> {
    await this.dbReady;
    if (!this.db) return 0;
    const now = Date.now();
    let count = 0;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const request = index.openCursor(IDBKeyRange.upperBound(now));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          this.memoryCache.delete(cursor.value.frameId);
          cursor.delete();
          count++;
          cursor.continue();
        } else {
          resolve(count);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async _cleanupOrphansForDoc(docId: string): Promise<number> {
    await this.dbReady;
    if (!this.db) return 0;
    let count = 0;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('docId');
      const request = index.openCursor(IDBKeyRange.only(docId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const entry = cursor.value as SnapshotEntry;
          if (!this.knownFrameIds.has(entry.frameId)) {
            this.memoryCache.delete(entry.frameId);
            cursor.delete();
            count++;
          }
          cursor.continue();
        } else {
          resolve(count);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async _enforceMaxEntries(): Promise<number> {
    const entries = await this._getAllFromDB();

    if (entries.length <= MAX_ENTRIES) {
      return 0;
    }

    // Sort by createdAt (oldest first)
    entries.sort((a, b) => a.createdAt - b.createdAt);

    const toDelete = entries.slice(0, entries.length - MAX_ENTRIES);
    let count = 0;

    for (const entry of toDelete) {
      this.memoryCache.delete(entry.frameId);
      await this._deleteFromDB(entry.frameId);
      count++;
    }

    return count;
  }
}

// Singleton instance
let instance: FrameSnapshotService | null = null;

export function getFrameSnapshotService(): FrameSnapshotService {
  if (!instance) {
    instance = new FrameSnapshotService();
  }
  return instance;
}
